import { z } from 'zod';

import {
  MAX_BULK_TARGETS,
  type TargetChip,
  type TargetType,
  targetChipSchema,
} from '@/forms/shared/targetChip';

import { resolveLocationTargets } from './targetResolution';

export { targetChipSchema };
export type { TargetChip, TargetType };

// Schema for service ticket creation
// Two target modes:
//  - "single": pick an existing case (which carries its location) OR a location directly (original flow)
//  - "id": one or more machine/location IDs are entered directly and tickets are bulk-created for all of them
export const serviceTicketCreateSchema = z
  .object({
    targetMode: z.enum(['single', 'id']).default('single'),
    case_id: z.string().optional(),
    location_id: z.string().optional(),
    targets: z
      .array(targetChipSchema)
      .max(MAX_BULK_TARGETS, `You can create at most ${MAX_BULK_TARGETS} service tickets at once`)
      .optional()
      .default([]),
    priority: z.string().min(1, 'Priority is required'),
    description: z.string().min(1, 'Description is required'),
    // Internal assigns a Nonce user (assigned_to), External an Ops Service (ops_service_id).
    // Switching the type clears the other field, so at most one of them is set.
    assignment_type: z.enum(['internal', 'external']).default('internal'),
    assigned_to: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => {
        if (val === undefined || val === '' || val === null) return undefined;
        return String(val);
      }),
    ops_service_id: z
      .string()
      .optional()
      .transform((val) => val || undefined),
    route: z.string().optional(),
    schedule_date: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.targetMode === 'single') {
      if (!data.case_id && !data.location_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either a case or a location must be selected',
          path: ['location_id'],
        });
      }
    } else {
      if (!data.targets || data.targets.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Add at least one machine or location ID',
          path: ['targets'],
        });
      } else if (data.targets.some((target) => target.status === 'invalid')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Remove invalid IDs before creating tickets',
          path: ['targets'],
        });
      } else if (data.targets.some((target) => target.status === 'validating')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please wait for ID validation to finish',
          path: ['targets'],
        });
      } else if (resolveLocationTargets(data.targets).unresolvable.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Remove machines with no installed location before creating tickets',
          path: ['targets'],
        });
      }
    }
  });

export type ServiceTicketCreateSchemaType = z.infer<typeof serviceTicketCreateSchema>;
